/**
 * The Smolit panel's state.
 *
 * One conversation per session, held here rather than in a route, because the
 * export draws Smolit as a dock over whatever screen you are on — asking about
 * a ride should not navigate away from it.
 *
 * The transcript the *model* sees is separate from the thread the *member*
 * sees, and deliberately so. The model's copy comes back from the server in
 * each turn's `done` event and is posted back verbatim on the next; the
 * member's copy is a list of bubbles, cards and results. Deriving one from the
 * other would mean either showing the member tool calls or hiding tool results
 * from the model.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router';
import { fetchSmolitConfig, streamTurn, type ConfirmationCard, type SmolitConfig } from './smolit';
import { useSession } from './session';

export interface Attachment {
  kind: string;
  data: unknown;
}

type CardState = 'open' | 'settling' | 'approved' | 'declined' | 'spent';

/**
 * How a turn ended, from the panel's point of view.
 *
 * `completed` — the server saw it through. `refused` — the server answered and
 * would not take it. `unreachable` — it never got there, so nothing about the
 * server's state can be inferred from it.
 */
type TurnOutcome = 'completed' | 'refused' | 'unreachable';

export type ThreadItem =
  | { kind: 'member'; id: string; text: string }
  | { kind: 'smolit'; id: string; text: string; attachments: Attachment[] }
  | {
      kind: 'card';
      id: string;
      approvalId: string;
      card: ConfirmationCard;
      /**
       * `settling` is the answer on its way. It exists because the other four
       * are all claims about what happened, and while the request is in flight
       * none of them is true yet.
       */
      state: 'open' | 'settling' | 'approved' | 'declined' | 'spent';
    };

interface SmolitState {
  open: boolean;
  thread: ThreadItem[];
  busy: boolean;
  /** What Smolit is doing right now — "Searching rides". */
  activity: string | null;
  config: SmolitConfig | null;
  openPanel: () => void;
  closePanel: () => void;
  toggle: () => void;
  send: (text: string) => void;
  decide: (approvalId: string, decision: 'approve' | 'decline') => void;
  reset: () => void;
}

const SmolitContext = createContext<SmolitState | null>(null);

/**
 * The three the export shows above the composer, word for word.
 *
 * They are also a fair test of the build: one searches, one prices a route, one
 * reads the member's own impact.
 */
export const EXAMPLE_PROMPTS = [
  'Find me a ride to Queenstown this Friday',
  'What’s a fair price Auckland to Tauranga?',
  'How much CO₂ have I saved?',
] as const;

const GREETING: ThreadItem = {
  kind: 'smolit',
  id: 'greeting',
  text:
    'Kia ora! I’m Smolit, your road-trip companion. Tell me where you’re headed and when, and ' +
    'I’ll find the best rides across Aotearoa.',
  attachments: [],
};

export function SmolitProvider({ children }: { children: ReactNode }) {
  const { member, loading } = useSession();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState<ThreadItem[]>([GREETING]);
  const [busy, setBusy] = useState(false);
  const [activity, setActivity] = useState<string | null>(null);
  const [config, setConfig] = useState<SmolitConfig | null>(null);

  /** The model's own transcript. Opaque here; posted straight back. */
  const transcript = useRef<unknown[]>([]);
  const nextId = useRef(0);
  const inFlight = useRef<AbortController | null>(null);

  const makeId = () => `item-${(nextId.current += 1)}`;

  /**
   * On opening, ask what is still awaiting an answer — and put it back.
   *
   * A confirmation card outlives the tab it was raised in; that is the whole
   * reason approvals are stored rather than held in memory. Reloading mid-decision
   * used to lose sight of it entirely: the action stayed answerable on the server
   * for ten minutes while the member had nothing to press.
   */
  useEffect(() => {
    if (!open || !member) return;
    const controller = new AbortController();

    fetchSmolitConfig(controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return;
        setConfig(next);
        setThread((items) => {
          const stillOpen = new Set(next.pending.map((entry) => entry.id));

          /**
           * The server is the authority on what is still answerable.
           *
           * A card can be settled here and open there — an approval that never
           * arrived leaves exactly that. Trusting the local copy hid the real
           * card behind the wrong one and left the member unable to answer it
           * at all, so the server's answer wins.
           */
          const reconciled = items.map((item) =>
            item.kind === 'card' && stillOpen.has(item.approvalId) && item.state !== 'settling'
              ? { ...item, state: 'open' as const }
              : item,
          );

          const known = new Set(
            reconciled.filter((item) => item.kind === 'card').map((item) => item.approvalId),
          );
          const restored = next.pending
            .filter((entry) => !known.has(entry.id))
            .map<ThreadItem>((entry) => ({
              kind: 'card',
              id: makeId(),
              approvalId: entry.id,
              card: entry.card,
              state: 'open',
            }));

          return restored.length > 0 ? [...reconciled, ...restored] : reconciled;
        });
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [open, member]);

  /**
   * Signing out must not leave the previous member's conversation on screen —
   * nor a turn of theirs still running against a session that has ended.
   *
   * `loading` is what makes this "signed out" rather than "not signed in yet".
   * On a fresh page load the session is restored from the refresh token, and
   * `member` is null for those first few hundred milliseconds — long enough
   * that opening the panel in that window had it close itself again.
   */
  useEffect(() => {
    if (loading || member) return;
    inFlight.current?.abort();
    inFlight.current = null;
    transcript.current = [];
    setThread([GREETING]);
    setOpen(false);
  }, [loading, member]);

  /**
   * Which screen the panel is open on, so "book this one" has a referent.
   *
   * The path is enough: `/trips/abc123` tells the model both the screen and the
   * ride, and it already has the ids of everything the member is involved in.
   */
  const screen = useMemo(() => location.pathname, [location.pathname]);

  const consume = useCallback(
    async (path: '/smolit/chat' | '/smolit/approve', body: unknown): Promise<TurnOutcome> => {
      inFlight.current?.abort();
      const controller = new AbortController();
      inFlight.current = controller;

      setBusy(true);
      setActivity(null);

      const replyId = makeId();
      let started = false;
      let finished = false;
      let refused = false;

      /** The reply bubble appears with its first word, not before it. */
      const ensureReply = () => {
        if (started) return;
        started = true;
        setThread((items) => [
          ...items,
          { kind: 'smolit', id: replyId, text: '', attachments: [] },
        ]);
      };

      const appendToReply = (update: (item: Extract<ThreadItem, { kind: 'smolit' }>) => void) => {
        ensureReply();
        setThread((items) =>
          items.map((item) => {
            if (item.id !== replyId || item.kind !== 'smolit') return item;
            const next = { ...item, attachments: [...item.attachments] };
            update(next);
            return next;
          }),
        );
      };

      try {
        for await (const event of streamTurn(path, body, controller.signal)) {
          switch (event.type) {
            case 'text':
              appendToReply((item) => {
                item.text += event.text;
              });
              break;

            case 'tool':
              setActivity(event.phase === 'start' ? event.label : null);
              break;

            case 'attachment':
              appendToReply((item) => {
                item.attachments.push({ kind: event.kind, data: event.data });
              });
              break;

            case 'confirmation':
              setThread((items) => {
                // The same card can arrive twice: raised by this stream, and
                // restored from `/smolit/config` if that answer lands first. One
                // approval is one decision, so it gets one card.
                const already = items.some(
                  (item) => item.kind === 'card' && item.approvalId === event.approvalId,
                );
                if (already) return items;

                return [
                  ...items,
                  {
                    kind: 'card',
                    id: makeId(),
                    approvalId: event.approvalId,
                    card: event.card,
                    state: 'open',
                  },
                ];
              });
              break;

            case 'error':
              // "No longer open" is the server refusing the answer itself, which
              // is different from the action failing — it means the card was
              // never claimable, so pressing it again would achieve nothing.
              if (/no longer open/i.test(event.message)) refused = true;
              appendToReply((item) => {
                item.text = item.text ? `${item.text}\n\n${event.message}` : event.message;
              });
              break;

            case 'done':
              transcript.current = event.messages;
              finished = true;
              break;
          }
        }
      } catch (error) {
        // An abort is the member closing the panel or asking something else;
        // it is not a failure worth showing them.
        if (!controller.signal.aborted) {
          appendToReply((item) => {
            item.text =
              item.text ||
              (error instanceof Error ? error.message : 'I could not reach the road from here.');
          });
        }
      } finally {
        if (inFlight.current === controller) {
          inFlight.current = null;
          setBusy(false);
          setActivity(null);
        }
      }

      if (refused) return 'refused';
      return finished ? 'completed' : 'unreachable';
    },
    [],
  );

  const send = useCallback(
    (text: string) => {
      const message = text.trim();
      if (!message || busy) return;

      setThread((items) => [...items, { kind: 'member', id: makeId(), text: message }]);
      void consume('/smolit/chat', {
        message,
        screen,
        transcript: transcript.current,
      });
    },
    [busy, consume, screen],
  );

  const setCardState = useCallback((approvalId: string, state: CardState) => {
    setThread((items) =>
      items.map((item) =>
        item.kind === 'card' && item.approvalId === approvalId ? { ...item, state } : item,
      ),
    );
  }, []);

  /**
   * Answer a card — and say so only once somebody authoritative has agreed.
   *
   * The buttons go the moment it is pressed, because a card that stays
   * pressable looks like it did nothing. But "Approved." is a claim about the
   * world, and it used to be made before the request had left the tab: a failed
   * approval left the panel reporting success while the action sat unanswered
   * on the server for ten minutes, and the restore-on-open dedup then hid the
   * real card behind the fake one.
   *
   * So the press moves the card to `settling`, and only the outcome moves it
   * anywhere else.
   */
  const decide = useCallback(
    (approvalId: string, decision: 'approve' | 'decline') => {
      if (busy) return;

      setCardState(approvalId, 'settling');

      void consume('/smolit/approve', {
        approvalId,
        decision,
        transcript: transcript.current,
      }).then((outcome) => {
        if (outcome === 'completed') {
          setCardState(approvalId, decision === 'approve' ? 'approved' : 'declined');
        } else if (outcome === 'refused') {
          // The server answered and would not take it — already used, declined
          // or expired. Not answerable, so not a button.
          setCardState(approvalId, 'spent');
        } else {
          // Never got there. The card is still open on the server, so it is
          // still open here.
          setCardState(approvalId, 'open');
        }
      });
    },
    [busy, consume, setCardState],
  );

  const reset = useCallback(() => {
    inFlight.current?.abort();
    transcript.current = [];
    setThread([GREETING]);
    setActivity(null);
    setBusy(false);
  }, []);

  /**
   * Closing stops the work.
   *
   * The panel is the only thing that will ever read that answer, so leaving the
   * turn running spends tokens on nobody and lets tool calls land behind a
   * closed door. The transcript keeps whatever completed, so reopening picks up
   * from a coherent point rather than mid-sentence.
   */
  const closePanel = useCallback(() => {
    inFlight.current?.abort();
    inFlight.current = null;
    setBusy(false);
    setActivity(null);
    setOpen(false);
  }, []);

  const openPanel = useCallback(() => setOpen(true), []);

  const value = useMemo<SmolitState>(
    () => ({
      open,
      thread,
      busy,
      activity,
      config,
      openPanel,
      closePanel,
      toggle: () => (open ? closePanel() : openPanel()),
      send,
      decide,
      reset,
    }),
    [open, thread, busy, activity, config, openPanel, closePanel, send, decide, reset],
  );

  return <SmolitContext.Provider value={value}>{children}</SmolitContext.Provider>;
}

export function useSmolit(): SmolitState {
  const context = useContext(SmolitContext);
  if (!context) throw new Error('useSmolit must be used inside <SmolitProvider>');
  return context;
}
